import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Loading from '../Components/Loading';

import { API_URL } from '../config/api';

const PayslipEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [formData, setFormData] = useState({
    // Earnings
    basic: '',
    da: '',
    hra: '',
    otherAllowance: '',
    specialAllowance: '',
    // Deductions
    pf: '',
    pfEe: '',
    pfEr: '',
    gratuity: '',
    esi: '',
    pt: '',
    tdsDeduction: '',
    otherDeduction: '',
    lopDeduction: '',
    // Totals / derived
    perMonthCtc: '',
    perDaySalary: '',
    grossEarnings: '',
    grossDeductions: '',
    netPay: '',
    // Attendance / counts
    stdDays: '',
    workedDays: '',
    lopDays: '',
    totalDaysInMonth: '',
    // Additional amounts
    arrearsAmount: '',
    statutoryBonus: ''
  });
  const [errors, setErrors] = useState({});
  const [focusedField, setFocusedField] = useState(null);
  const [employeeInfo, setEmployeeInfo] = useState({
    employeeName: '',
    employeeId: '',
    designationName: '',
    department: '',
    dateOfJoining: '',
    bankName: '',
    bankAccountNumber: '',
    pfNumber: '',
    pfUanNumber: '',
    esiNumber: '',
    panNumber: '',
    month: '',
    year: '',
    status: ''
  });

  useEffect(() => {
    if (!id) {
      toast.error('Payslip identifier is missing.');
      navigate('/payslip-approval');
      return;
    }
    fetchPayslipDetails(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Fetch existing payslip details
  const fetchPayslipDetails = async (payslipId) => {
    if (!API_URL) {
      toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
      setLoadingData(false);
      return;
    }

    const currentUserId = localStorage.getItem('id');
    if (!currentUserId) {
      toast.error('User ID not found. Please login again.');
      setLoadingData(false);
      navigate('/payslip-approval');
      return;
    }

    setLoadingData(true);
    try {
      const payload = {
        action: 'get-payslip',
        currentUserId: currentUserId,
        id: payslipId
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;
      if (result?.success && result?.data) {
        const data = result.data;
        const toInputValue = (val) => {
          if (val === null || val === undefined || val === '') return '';
          return String(val);
        };
        const pfEeValue = data.pfEe ?? data.pf;
        // Populate form with existing data
        setFormData({
          basic: toInputValue(data.basic),
          da: toInputValue(data.da),
          hra: toInputValue(data.hra),
          otherAllowance: toInputValue(data.otherAllowance),
          specialAllowance: toInputValue(data.specialAllowance),
          pf: toInputValue(pfEeValue),
          pfEe: toInputValue(pfEeValue),
          pfEr: toInputValue(data.pfEr),
          gratuity: toInputValue(data.gratuity),
          esi: toInputValue(data.esi),
          pt: toInputValue(data.pt),
          tdsDeduction: toInputValue(data.tdsDeduction),
          otherDeduction: toInputValue(data.otherDeduction),
          lopDeduction: toInputValue(data.lopDeduction),
          perMonthCtc: toInputValue(data.perMonthCtc),
          perDaySalary: toInputValue(data.perDaySalary),
          grossEarnings: toInputValue(data.grossEarnings),
          grossDeductions: toInputValue(data.grossDeductions),
          netPay: toInputValue(data.netPay),
          stdDays: toInputValue(data.stdDays),
          workedDays: toInputValue(data.workedDays),
          lopDays: toInputValue(data.lopDays),
          totalDaysInMonth: toInputValue(data.totalDaysInMonth),
          arrearsAmount: toInputValue(data.arrearsAmount),
          statutoryBonus: toInputValue(data.statutoryBonus)
        });
        // Store employee info for display
        setEmployeeInfo({
          employeeName: data.employeeName || '',
          employeeId: data.employeeId || '',
          designationName: data.designationName || '',
          department: data.department || '',
          dateOfJoining: data.dateOfJoining || '',
          bankName: data.bankName || '',
          bankAccountNumber: data.bankAccountNumber || '',
          pfNumber: data.pfNumber || '',
          pfUanNumber: data.pfUanNumber || '',
          esiNumber: data.esiNumber || '',
          panNumber: data.panNumber || '',
          month: data.month || '',
          year: data.year || '',
          status: data.status || ''
        });
      } else {
        toast.error(result?.message || 'Failed to fetch payslip details');
        navigate('/payslip-approval');
      }
    } catch (error) {
      console.error('Error fetching payslip details:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
      navigate('/payslip-approval');
    } finally {
      setLoadingData(false);
    }
  };

  // Format number for display (with commas and 2 decimals)
  const formatNumberForDisplay = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const numValue = parseFloat(value.toString().replace(/,/g, ''));
    if (isNaN(numValue)) return value;
    return numValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  // Get display value - formatted if not focused, raw if focused
  const getDisplayValue = (name, value) => {
    const numericFields = [
      'basic', 'da', 'hra', 'otherAllowance', 'specialAllowance',
      'pf', 'pfEe', 'pfEr', 'gratuity', 'esi', 'pt', 'tdsDeduction', 'otherDeduction', 'lopDeduction',
      'perMonthCtc', 'perDaySalary', 'grossEarnings', 'grossDeductions', 'netPay',
      'stdDays', 'workedDays', 'lopDays', 'totalDaysInMonth'
    ];
    
    if (numericFields.includes(name)) {
      if (focusedField === name) {
        return value || '';
      }
      return formatNumberForDisplay(value);
    }
    return value || '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let validatedValue = value;

    // Validate numeric fields - only allow numbers and decimal point
    const numericFields = [
      'basic', 'da', 'hra', 'otherAllowance', 'specialAllowance',
      'pf', 'pfEe', 'pfEr', 'gratuity', 'esi', 'pt', 'tdsDeduction', 'otherDeduction', 'lopDeduction',
      'perMonthCtc', 'perDaySalary', 'grossEarnings', 'grossDeductions', 'netPay',
      'stdDays', 'workedDays', 'lopDays', 'totalDaysInMonth'
    ];

    if (numericFields.includes(name)) {
      // Remove commas first, then allow numbers and one decimal point
      let cleaned = value.replace(/,/g, '');
      cleaned = cleaned.replace(/[^\d.]/g, '');
      // Ensure only one decimal point
      const parts = cleaned.split('.');
      if (parts.length > 2) {
        cleaned = parts[0] + '.' + parts.slice(1).join('');
      }
      validatedValue = cleaned;
    }

    setFormData(prev => ({
      ...prev,
      [name]: validatedValue
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Auto-calculate derived values after state update
    setTimeout(() => {
      setFormData(prev => {
        const updatedData = { ...prev, [name]: validatedValue };
        
        const num = (val) => parseFloat((val || '').toString().replace(/,/g, '')) || 0;

        // Derived: perDaySalary from perMonthCtc and totalDaysInMonth
        const totalDays = num(updatedData.totalDaysInMonth);
        const perMonth = num(updatedData.perMonthCtc);
        if (totalDays > 0 && perMonth >= 0) {
          updatedData.perDaySalary = (perMonth / totalDays).toFixed(2);
        }

        // If lopDays changes and we have perDaySalary, recompute lopDeduction
        if (name === 'lopDays' || name === 'perDaySalary' || name === 'totalDaysInMonth' || name === 'perMonthCtc') {
          const lopDaysVal = num(updatedData.lopDays);
          const perDay = num(updatedData.perDaySalary);
          if (lopDaysVal >= 0 && perDay >= 0) {
            updatedData.lopDeduction = (lopDaysVal * perDay).toFixed(2);
          }
        }

        // Calculate totals
        const totalEarnings = num(updatedData.basic) + num(updatedData.da) + num(updatedData.hra)
          + num(updatedData.otherAllowance) + num(updatedData.specialAllowance) + num(updatedData.arrearsAmount) + num(updatedData.statutoryBonus);

        if (name === 'pfEe') {
          updatedData.pf = validatedValue;
        }

        const totalDeductions = num(updatedData.pfEe) + num(updatedData.esi) + num(updatedData.pt)
          + num(updatedData.tdsDeduction) + num(updatedData.otherDeduction) + num(updatedData.lopDeduction);

        updatedData.grossEarnings = totalEarnings > 0 ? totalEarnings.toFixed(2) : '';
        updatedData.grossDeductions = totalDeductions > 0 ? totalDeductions.toFixed(2) : '';

        const netPay = totalEarnings - totalDeductions;
        updatedData.netPay = netPay > 0 ? netPay.toFixed(2) : '0.00';

        return updatedData;
      });
    }, 0);
  };

  const handleFocus = (e) => {
    const { name } = e.target;
    setFocusedField(name);
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setFocusedField(null);
    
    // Format the value on blur
    const numericFields = [
      'basic', 'da', 'hra', 'otherAllowance', 'specialAllowance',
      'pf', 'pfEe', 'pfEr', 'gratuity', 'esi', 'pt', 'tdsDeduction', 'otherDeduction', 'lopDeduction',
      'perMonthCtc', 'perDaySalary', 'grossEarnings', 'grossDeductions', 'netPay',
      'stdDays', 'workedDays', 'lopDays', 'totalDaysInMonth'
    ];
    
    if (numericFields.includes(name)) {
      const value = formData[name];
      if (value && value !== '') {
        const numValue = parseFloat(value.toString().replace(/,/g, ''));
        if (!isNaN(numValue)) {
          setFormData(prev => ({
            ...prev,
            [name]: numValue.toFixed(2)
          }));
        }
      }
    }
  };

  const getMonthName = (monthNumber) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[monthNumber - 1] || '';
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.basic || formData.basic === '') {
      newErrors.basic = 'Basic salary is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fill all required fields');
      return;
    }

    if (!API_URL) {
      toast.error('API URL is not configured. Please set REACT_APP_API_URL in environment variables.');
      return;
    }

    const currentUserId = localStorage.getItem('id');
    
    if (!currentUserId) {
      toast.error('User ID not found. Please login again.');
      return;
    }

    setLoading(true);
    try {
      const cleanNumericValue = (val) => {
        if (val === null || val === undefined || val === '') return '';
        const cleaned = val.toString().replace(/,/g, '');
        return cleaned;
      };

      const toNumberValue = (val) => {
        const cleaned = cleanNumericValue(val);
        if (cleaned === '') return '';
        const num = parseFloat(cleaned);
        return isNaN(num) ? '' : num;
      };

      const payload = {
        action: 'update-payslip',
        id: id,
        currentUserId: currentUserId,
        basic: cleanNumericValue(formData.basic),
        da: cleanNumericValue(formData.da),
        hra: cleanNumericValue(formData.hra),
        otherAllowance: cleanNumericValue(formData.otherAllowance),
        specialAllowance: cleanNumericValue(formData.specialAllowance),
        pf: cleanNumericValue(formData.pfEe || formData.pf),
        pfEe: cleanNumericValue(formData.pfEe),
        pfEr: cleanNumericValue(formData.pfEr),
        gratuity: cleanNumericValue(formData.gratuity),
        esi: cleanNumericValue(formData.esi),
        pt: cleanNumericValue(formData.pt),
        tdsDeduction: toNumberValue(formData.tdsDeduction),
        otherDeduction: toNumberValue(formData.otherDeduction),
        lopDeduction: cleanNumericValue(formData.lopDeduction),
        grossEarnings: cleanNumericValue(formData.grossEarnings),
        grossDeductions: cleanNumericValue(formData.grossDeductions),
        netPay: cleanNumericValue(formData.netPay),
        arrearsAmount: cleanNumericValue(formData.arrearsAmount),
        statutoryBonus: cleanNumericValue(formData.statutoryBonus),
        stdDays: cleanNumericValue(formData.stdDays),
        workedDays: cleanNumericValue(formData.workedDays),
        lopDays: cleanNumericValue(formData.lopDays),
        totalDaysInMonth: cleanNumericValue(formData.totalDaysInMonth),
        perMonthCtc: cleanNumericValue(formData.perMonthCtc),
        perDaySalary: cleanNumericValue(formData.perDaySalary)
      };

      const response = await axios.post(API_URL, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;

      if (result?.success) {
        toast.success(result.message || 'Payslip updated successfully');
        setTimeout(() => {
          navigate('/payslip-approval');
        }, 1500);
      } else {
        toast.error(result?.message || 'Failed to update payslip');
      }
    } catch (error) {
      console.error('Error updating payslip:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Something went wrong. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loadingData) {
    return (
      <div className="employee-form">
        <section className="content">
          <div className="container-fluid">
            <div className="card employee-content-card">
              <div className="card-body">
                <Loading size={100} />
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="employee-form">
      <section className="content">
        <div className="container-fluid">
          {/* Header with Title */}
          <div className="row mb-3 employee-header-row" style={{ alignItems: 'center' }}>
            <div className="col-md-8">
              <h1 className="employee-title">
                Edit Payslip
                <i className="fas fa-info-circle ml-2 employee-title-icon"></i>
              </h1>
              <p className="employee-description">
                Update payslip details for {employeeInfo.employeeName || 'employee'} - {getMonthName(employeeInfo.month)} {employeeInfo.year}
              </p>
            </div>
            <div className="col-md-4" style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="employee-back-btn"
                onClick={() => navigate('/payslip-approval')}
              >
                <i className="fas fa-arrow-left mr-2"></i>
                Back
              </button>
            </div>
          </div>

          {/* Form Card */}
          <div className="card employee-content-card">
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                {/* Employee Information Section */}
                <div className="mb-4">
                  <h4 className="section-title">Employee Information</h4>
                  <p className="section-subtitle">Payslip details for the selected employee</p>

                  <div className="row g-3">
                    <div className="col-md-4">
                      <label htmlFor="employeeName" className="form-label">Employee Name</label>
                      <input type="text" id="employeeName" className="form-control" value={employeeInfo.employeeName} readOnly style={{ backgroundColor: '#f8f9fa' }} />
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="employeeId" className="form-label">Employee ID</label>
                      <input type="text" id="employeeId" className="form-control" value={employeeInfo.employeeId} readOnly style={{ backgroundColor: '#f8f9fa' }} />
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="designationName" className="form-label">Designation</label>
                      <input type="text" id="designationName" className="form-control" value={employeeInfo.designationName} readOnly style={{ backgroundColor: '#f8f9fa' }} />
                    </div>
                  </div>

                  <div className="row g-3 mt-1">
                    <div className="col-md-4">
                      <label htmlFor="department" className="form-label">Department</label>
                      <input type="text" id="department" className="form-control" value={employeeInfo.department} readOnly style={{ backgroundColor: '#f8f9fa' }} />
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="monthYear" className="form-label">Month & Year</label>
                      <input type="text" id="monthYear" className="form-control" value={`${getMonthName(employeeInfo.month)} ${employeeInfo.year}`} readOnly style={{ backgroundColor: '#f8f9fa' }} />
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="dateOfJoining" className="form-label">Date of Joining</label>
                      <input type="text" id="dateOfJoining" className="form-control" value={employeeInfo.dateOfJoining ? new Date(employeeInfo.dateOfJoining).toLocaleDateString() : ''} readOnly style={{ backgroundColor: '#f8f9fa' }} />
                    </div>
                  </div>

                  <div className="row g-3 mt-1">
                    <div className="col-md-4">
                      <label className="form-label">Status</label>
                      <div className="form-control" style={{ backgroundColor: '#f8f9fa', fontWeight: 600 }}>
                        {employeeInfo.status ? employeeInfo.status.toUpperCase() : 'PENDING'}
                      </div>
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="bankName" className="form-label">Bank Name</label>
                      <input type="text" id="bankName" className="form-control" value={employeeInfo.bankName} readOnly style={{ backgroundColor: '#f8f9fa' }} />
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="bankAccountNumber" className="form-label">Bank Account Number</label>
                      <input type="text" id="bankAccountNumber" className="form-control" value={employeeInfo.bankAccountNumber} readOnly style={{ backgroundColor: '#f8f9fa' }} />
                    </div>
                  </div>

                  <div className="row g-3 mt-1">
                    <div className="col-md-4">
                      <label htmlFor="pfUanNumber" className="form-label">PF UAN Number</label>
                      <input type="text" id="pfUanNumber" className="form-control" value={employeeInfo.pfUanNumber} readOnly style={{ backgroundColor: '#f8f9fa' }} />
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="esiNumber" className="form-label">ESI Number</label>
                      <input type="text" id="esiNumber" className="form-control" value={employeeInfo.esiNumber} readOnly style={{ backgroundColor: '#f8f9fa' }} />
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="pfNumber" className="form-label">PF Number</label>
                      <input type="text" id="pfNumber" className="form-control" value={employeeInfo.pfNumber} readOnly style={{ backgroundColor: '#f8f9fa' }} />
                    </div>
                  </div>

                  <div className="row g-3 mt-1">
                    <div className="col-md-4">
                      <label htmlFor="panNumber" className="form-label">PAN Number</label>
                      <input type="text" id="panNumber" className="form-control" value={employeeInfo.panNumber} readOnly style={{ backgroundColor: '#f8f9fa' }} />
                    </div>
                    <div className="col-md-4"></div>
                    <div className="col-md-4"></div>
                  </div>
                </div>

                {/* Attendance & Pay Basis */}
                <div className="mb-4">
                  <h4 className="section-title">Attendance & Pay Basis</h4>
                  <p className="section-subtitle">Adjust the working days and review per-day calculations</p>

                  <div className="row g-3">
                    <div className="col-md-4">
                      <label htmlFor="stdDays" className="form-label">Standard Days</label>
                      <input type="text" id="stdDays" name="stdDays" className="form-control" value={getDisplayValue('stdDays', formData.stdDays)} onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur} placeholder="Enter standard days" />
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="workedDays" className="form-label">Worked Days</label>
                      <input type="text" id="workedDays" name="workedDays" className="form-control" value={getDisplayValue('workedDays', formData.workedDays)} onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur} placeholder="Enter worked days" />
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="lopDays" className="form-label">LOP Days</label>
                      <input type="text" id="lopDays" name="lopDays" className="form-control" value={getDisplayValue('lopDays', formData.lopDays)} onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur} placeholder="Enter LOP days" />
                    </div>
                  </div>

                  <div className="row g-3 mt-1">
                    <div className="col-md-4">
                      <label htmlFor="arrearsAmount" className="form-label">Arrears Amount</label>
                      <input type="text" id="arrearsAmount" name="arrearsAmount" className="form-control" value={getDisplayValue('arrearsAmount', formData.arrearsAmount)} onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur} placeholder="Enter arrears amount" />
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="statutoryBonus" className="form-label">Statutory Bonus</label>
                      <input type="text" id="statutoryBonus" name="statutoryBonus" className="form-control" value={getDisplayValue('statutoryBonus', formData.statutoryBonus)} onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur} placeholder="Enter statutory bonus" />
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="perMonthCtc" className="form-label">Per Month CTC</label>
                      <input type="text" id="perMonthCtc" name="perMonthCtc" className="form-control" value={getDisplayValue('perMonthCtc', formData.perMonthCtc)} readOnly style={{ backgroundColor: '#f8f9fa' }} />
                    </div>
                  </div>

                  <div className="row g-3 mt-1">
                    <div className="col-md-4">
                      <label htmlFor="totalDaysInMonth" className="form-label">Total Days in Month</label>
                      <input type="text" id="totalDaysInMonth" name="totalDaysInMonth" className="form-control" value={getDisplayValue('totalDaysInMonth', formData.totalDaysInMonth)} onChange={handleChange} onFocus={handleFocus} onBlur={handleBlur} placeholder="Enter total days in month" />
                    </div>
                    <div className="col-md-4">
                      <label htmlFor="perDaySalary" className="form-label">Per Day Salary</label>
                      <input type="text" id="perDaySalary" name="perDaySalary" className="form-control" value={getDisplayValue('perDaySalary', formData.perDaySalary)} readOnly style={{ backgroundColor: '#f8f9fa' }} />
                    </div>
                    <div className="col-md-4"></div>
                  </div>
                </div>

                {/* Earnings Section */}
                <div className="mb-4">
                  <h4 className="section-title">Earnings</h4>
                  <p className="section-subtitle">
                    Enter earnings components
                  </p>

                  <div className="form-row-3">
                    <div className="form-group">
                      <label htmlFor="basic" className="form-label">
                        Basic<span className="required-star">*</span>
                      </label>
                      <input
                        type="text"
                        id="basic"
                        name="basic"
                        className={`form-control ${errors.basic ? 'is-invalid' : ''}`}
                        value={getDisplayValue('basic', formData.basic)}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        placeholder="Enter basic salary"
                      />
                      {errors.basic && (
                        <div className="invalid-feedback">{errors.basic}</div>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="da" className="form-label">DA (Dearness Allowance)</label>
                      <input
                        type="text"
                        id="da"
                        name="da"
                        className="form-control"
                        value={getDisplayValue('da', formData.da)}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        placeholder="Enter DA"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="hra" className="form-label">HRA (House Rent Allowance)</label>
                      <input
                        type="text"
                        id="hra"
                        name="hra"
                        className="form-control"
                        value={getDisplayValue('hra', formData.hra)}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        placeholder="Enter HRA"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="otherAllowance" className="form-label">Other Allowance</label>
                      <input
                        type="text"
                        id="otherAllowance"
                        name="otherAllowance"
                        className="form-control"
                        value={getDisplayValue('otherAllowance', formData.otherAllowance)}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        placeholder="Enter other allowance"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="specialAllowance" className="form-label">Special Allowance</label>
                      <input
                        type="text"
                        id="specialAllowance"
                        name="specialAllowance"
                        className="form-control"
                        value={getDisplayValue('specialAllowance', formData.specialAllowance)}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        placeholder="Enter special allowance"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="grossEarnings" className="form-label">Gross Earnings</label>
                      <input
                        type="text"
                        id="grossEarnings"
                        name="grossEarnings"
                        className="form-control"
                        value={getDisplayValue('grossEarnings', formData.grossEarnings)}
                        readOnly
                        style={{ backgroundColor: '#f8f9fa', fontWeight: 600 }}
                      />
                    </div>
                  </div>
                </div>

                {/* Deductions Section */}
                <div className="mb-4">
                  <h4 className="section-title">Deductions</h4>
                  <p className="section-subtitle">
                    Enter deduction components
                  </p>

                  <div className="form-row-3">
                    <div className="form-group">
                      <label htmlFor="pfEe" className="form-label">Provident Fund (EE)</label>
                      <input
                        type="text"
                        id="pfEe"
                        name="pfEe"
                        className="form-control"
                        value={getDisplayValue('pfEe', formData.pfEe)}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        placeholder="Enter PF (Employee)"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="pfEr" className="form-label">Provident Fund (ER)</label>
                      <input
                        type="text"
                        id="pfEr"
                        name="pfEr"
                        className="form-control"
                        value={getDisplayValue('pfEr', formData.pfEr)}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        placeholder="Enter PF (Employer)"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="gratuity" className="form-label">Gratuity</label>
                      <input
                        type="text"
                        id="gratuity"
                        name="gratuity"
                        className="form-control"
                        value={getDisplayValue('gratuity', formData.gratuity)}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        placeholder="Enter gratuity"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="esi" className="form-label">ESI</label>
                      <input
                        type="text"
                        id="esi"
                        name="esi"
                        className="form-control"
                        value={getDisplayValue('esi', formData.esi)}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        placeholder="Enter ESI"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="pt" className="form-label">PT (Professional Tax)</label>
                      <input
                        type="text"
                        id="pt"
                        name="pt"
                        className="form-control"
                        value={getDisplayValue('pt', formData.pt)}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        placeholder="Enter PT"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="tdsDeduction" className="form-label">TDS Deduction</label>
                      <input
                        type="text"
                        id="tdsDeduction"
                        name="tdsDeduction"
                        className="form-control"
                        value={getDisplayValue('tdsDeduction', formData.tdsDeduction)}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        placeholder="Enter TDS deduction"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="otherDeduction" className="form-label">Other Deduction</label>
                      <input
                        type="text"
                        id="otherDeduction"
                        name="otherDeduction"
                        className="form-control"
                        value={getDisplayValue('otherDeduction', formData.otherDeduction)}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        placeholder="Enter other deduction"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="lopDeduction" className="form-label">LOP Deduction</label>
                      <input
                        type="text"
                        id="lopDeduction"
                        name="lopDeduction"
                        className="form-control"
                        value={getDisplayValue('lopDeduction', formData.lopDeduction)}
                        onChange={handleChange}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        placeholder="Enter LOP deduction"
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="grossDeductions" className="form-label">Gross Deductions</label>
                      <input
                        type="text"
                        id="grossDeductions"
                        name="grossDeductions"
                        className="form-control"
                        value={getDisplayValue('grossDeductions', formData.grossDeductions)}
                        readOnly
                        style={{ backgroundColor: '#f8f9fa', fontWeight: 600 }}
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="netPay" className="form-label">Net Pay</label>
                      <input
                        type="text"
                        id="netPay"
                        name="netPay"
                        className="form-control"
                        value={getDisplayValue('netPay', formData.netPay)}
                        placeholder="Auto calculated"
                        readOnly
                        style={{ fontWeight: '600', color: '#28a745', backgroundColor: '#f8f9fa' }}
                      />
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="form-actions">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => navigate('/payslip-approval')}
                    disabled={loading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span>Updating...</span>
                      </>
                    ) : (
                      <>
                        <span>Update Payslip</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PayslipEdit;
